import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import journalApi, { StrategyItem } from '../../services/journalApi.ts';
import './BulkUploadModal.css';

interface Props {
  strategies: StrategyItem[];
  onClose:    () => void;
  onDone:     () => void;
}

const BulkUploadModal: React.FC<Props> = ({ strategies, onClose, onDone }) => {
  const [strategyId, setStrategyId] = useState<number | ''>('');
  const [file,       setFile]       = useState<File | null>(null);
  const [loading,    setLoading]    = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported');
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!strategyId) { toast.error('Please select a strategy'); return; }
    if (!file)       { toast.error('Please select an Excel file'); return; }

    setLoading(true);
    try {
      const res = await journalApi.bulkUpload(file, strategyId as number);
      const { saved, total } = res.data.data;
      toast.success(`${saved} of ${total} trades imported successfully`);
      onDone();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-modal" onClick={e => e.stopPropagation()}>

        <div className="bulk-header">
          <div className="bulk-title">Bulk Import — Upstox P&amp;L</div>
          <button className="bulk-close" onClick={onClose}>✕</button>
        </div>

        <div className="bulk-body">

          {/* Info banner */}
          <div className="bulk-info">
            Upload your <strong>Upstox Realized P&amp;L (.xlsx)</strong> report.
            All trades will be imported as <strong>NSE · EQUITY · LONG · SWING</strong>.
          </div>

          {/* Strategy */}
          <div className="bulk-field">
            <label className="bulk-label">Strategy <span className="req">*</span></label>
            <select
              className="bulk-select"
              value={strategyId}
              onChange={e => setStrategyId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Select strategy…</option>
              {strategies.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* File picker */}
          <div className="bulk-field">
            <label className="bulk-label">Excel File <span className="req">*</span></label>
            <div className="bulk-file-wrap" onClick={() => fileRef.current?.click()}>
              <span className="bulk-file-icon">📂</span>
              <span className="bulk-file-name">
                {file ? file.name : 'Click to select .xlsx file…'}
              </span>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
          </div>

          {/* Defaults summary */}
          <div className="bulk-defaults">
            <span>Exchange: <strong>NSE</strong></span>
            <span>Direction: <strong>LONG</strong></span>
            <span>Type: <strong>SWING</strong></span>
            <span>Mode: <strong>TRADE</strong></span>
          </div>

        </div>

        <div className="bulk-footer">
          <button className="bulk-btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="bulk-btn-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Importing…' : 'Import Trades'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default BulkUploadModal;
